import React, { createContext, useContext, useState, useEffect } from "react";
import { addonInvoicePreview, addonSellerAccount, createPlan, previewPlanSummary, reGeneratePaymentUrl, upgradePlan } from "../services/billing";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { is } from "date-fns/locale";

const RazorpayContext = createContext();

export const RazorpayProvider = ({ children }) => {
  const navigate = useNavigate();
  const [razorpayModalData, setRazorpayModalData] = useState({
    shortUrl: null,
    invoiceId: null,
    isLoading: false,
    isRazorPayModal: false,
    isPayment: null,
    isBlockedRedirection: false,
    refetchDataFn: null,
    isPaymentExpired: false
  })

  const [invoiceModalData, setInvoiceModalData] = useState({
    isInvoiceDetailsModal: false,
    invoiceDetails: null,
    isLoading: false
  })

  const [addonInvoicePreviewData, setAddonPreviewData] = useState({
    isOpenModal: false,
    previewData: null,
    isLoading: false
  })

  const [isRazorpayModalTimerLoader, setIsRazorpayModalTimerLoader] = useState(false)
  const [isPaymentStatus, setPaymentStatus] = useState(null)
  const [packageId, setPackageId] = useState(null)
  const existingRazorpayProcessingData = JSON.parse(localStorage.getItem('razorpayModalData') || "null");
  const isLogedIn = localStorage.getItem('token');

  useEffect(() => {
    if (existingRazorpayProcessingData) {
      setRazorpayModalData((prev) => ({
        ...prev,
        shortUrl: existingRazorpayProcessingData?.shortUrl,
        invoiceId: existingRazorpayProcessingData?.invoiceId,
        isLoading: existingRazorpayProcessingData?.isLoading,
        isRazorPayModal: existingRazorpayProcessingData?.isRazorPayModal,
        isBlockedRedirection: true
      }));
    }
  }, []);

  const openPaymentTab = (shortUrl, invoiceId, refetchBilling, isPaymentExpired = false) => {
    const newTab = window.open(shortUrl, "_blank");
    if (!newTab || newTab.closed || typeof newTab.closed === 'undefined') {
      setRazorpayModalData(prev => ({ ...prev, isBlockedRedirection: true }));
    }
    setRazorpayModalData(prev => ({
      ...prev,
      shortUrl,
      invoiceId,
      isLoading: true,
      isRazorPayModal: true,
      refetchDataFn: refetchBilling,
      isPaymentExpired: isPaymentExpired
    }));
    setIsRazorpayModalTimerLoader(true);
  };

  const handleCreatePlan = async (e, setIsLoading, packageType, currentPlan, refetchBilling,paymentMethod) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let apiRes;
      if (packageType === 'upgrade') {
        apiRes = await upgradePlan({ package_id: packageId });
        const isPaid = apiRes?.data?.data?.paid_plan;

        if (isPaid) {
          const shortUrl = apiRes?.data?.data?.short_url;
          if (!shortUrl) {
            toast.error("Unable to proceed with the payment. Please contact support or try again after 15 minutes.");
            return;
          }
          openPaymentTab(shortUrl, apiRes?.data?.data?.invoice?.id);
        } else {
          toast.success(
            apiRes?.data?.message ||
            'Your upgrade plan request received successfully. Waiting for payment confirmation.',
            { position: "bottom-right" }
          );
          typeof refetchBilling === 'function' && refetchBilling();
        }
      }
      else if (packageType === 'generatePaymentUrl') {
        apiRes = await reGeneratePaymentUrl({payment_method:paymentMethod?.toLowerCase()});
        let shortUrl, invoiceId;
        shortUrl = apiRes?.data?.data?.short_url;
        invoiceId = apiRes?.data?.data?.invoice?.id;
        if (!shortUrl) {
          toast.error("Unable to proceed with the payment. Please contact support or try again after 15 minutes.");
          return;
        }
        // Open payment tab if shortUrl exists
        if (shortUrl) openPaymentTab(shortUrl, invoiceId, refetchBilling);
      }
      else { // pendingPayment or new plan
        let shortUrl, invoiceId;

        if (packageType === 'pendingPayment' && currentPlan) {
          shortUrl = currentPlan?.subscription?.short_url;
          invoiceId = currentPlan?.subscription?.invoice_id;

          if (!shortUrl) {
            toast.error("Unable to proceed with the payment. Please contact support or try again after 15 minutes.");
            return;
          }
        } else { // new plan
          apiRes = await createPlan({ package: { id: packageId },payment_method:paymentMethod?.toLowerCase() });
          const isPaid = apiRes?.data?.data?.paid_plan;
          if (!isPaid) {
            toast.success(apiRes?.data?.message || 'Your subscription plan created successfully', {
              position: "bottom-right"
            });
            window.dispatchEvent(new Event("refresh-page"));
            return;
          }

          shortUrl = apiRes?.data?.data?.short_url;
          invoiceId = apiRes?.data?.data?.invoice?.id;

          if (!shortUrl) {
            toast.error("Unable to proceed with the payment. Please contact support or try again after 15 minutes.");
            return;
          }
        }

        // Open payment tab if shortUrl exists
        if (shortUrl) openPaymentTab(shortUrl, invoiceId, null, true);
      }
    } catch (err) {
      console.error('Failed to create plan API:', err);
      // toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
      setInvoiceModalData(prev => ({ ...prev, isInvoiceDetailsModal: false }));
    }
  };

  const handlePreviewPlan = async (
    e,
    packageId,
    packageType,
    setTraceBtnClick,
    isContact = false
  ) => {
    e.preventDefault();
    if (isContact) {
      navigate('/contact');
      return;
    }
    // Early return for unauthenticated user
    if (!isLogedIn) {
      navigate('/signin');
      return;
    }

    const isPlan = packageType === "plan" || packageType=='paymentMethod';

    // Start loading states
    setInvoiceModalData((prev) => ({ ...prev, isLoading: true }));
    setPaymentStatus(null)
    if (isPlan && setTraceBtnClick) {
      setTraceBtnClick({ isLoading: true, btnClickPackageId: packageId });
    }

    try {
      const payload = {
        package_id: packageId,
        invoice_preview_for: packageType === "upgrade" ? "upgrade_plan" : "plan",
      };

      setPackageId(packageId);

      const { data } = await previewPlanSummary(payload);

      setInvoiceModalData((prev) => ({
        ...prev,
        isInvoiceDetailsModal: true,
        invoiceDetails: data?.data,
        planType: packageType 
      }));

      // packageType=='paymentMethod' ? 'Continue' : 'Start Free Trial'
    } catch (err) {
      console.error("Failed to call create plan API:", err);
    } finally {
      // Reset loading states
      setInvoiceModalData((prev) => ({ ...prev, isLoading: false }));
      if (isPlan && setTraceBtnClick) {
        setTraceBtnClick({ isLoading: false, btnClickPackageId: null });
      }
    }
  };

  const onClosePackageSummaryModal = () => {
    setInvoiceModalData((prev) => ({
      ...prev,
      isInvoiceDetailsModal: false
    }))
  }

  const handleFailed = (failedNavigationUrl) => {
    setRazorpayModalData((prev) => ({
      ...prev,
      shortUrl: null,
      invoiceId: null,
      isLoading: false,
      isRazorPayModal: false,
      isBlockedRedirection: false,
      isPaymentExpired: false
    }))
    setInvoiceModalData((prev) => ({
      ...prev,
      isInvoiceDetailsModal: false,
      invoiceDetails: null,
      isLoading: false
    }))

    if (failedNavigationUrl) {
      navigate(failedNavigationUrl)
    }
    if (currentPath === '/pricing') {
      window.dispatchEvent(new Event("refresh-page"));
    }
    else {
      navigate('/pricing')
    }
  }

  const handleAddOnInvoicePreview = async (setLoading, marketplaceAccountId) => {
    try {
      setLoading && setLoading(true)
      const res = await addonInvoicePreview(marketplaceAccountId)
      setAddonPreviewData((prev) => ({
        ...prev,
        isOpenModal: true,
        previewData: res?.data?.invoice
      }))
    }
    catch (err) {
      console.error('Failed to called addon invoice preview : ', err);
      toast.error(err?.message || err?.data?.message || 'Invoice preview failed', {
        position: "bottom-right"
      })
    }
    finally {
      setLoading && setLoading(false)
    }
  }

  const handleAddOnSellerAccount = async (e, marketplaceAccounts, submitArg) => {
    try {
      e.preventDefault();
      setAddonPreviewData((prev) => ({
        ...prev,
        isLoading: true,
      }))
      const marketplaceAccountIds = Array.isArray(marketplaceAccounts) ? marketplaceAccounts.map((account) => account?.id) : marketplaceAccounts;
      const apiRes = await addonSellerAccount(marketplaceAccountIds)
      setAddonPreviewData((prev) => ({
        ...prev,
        isOpenModal: false,
        previewData: null,
      }))
      const isPaid = apiRes?.data?.data?.paid_plan;
      const isPaymentLink = apiRes?.data?.payment_link;
      if (isPaymentLink) {
        window.history.pushState(null, "", window.location.href);
        window.onpopstate = function () {
          window.history.pushState(null, "", window.location.href);
        };
        window.open(isPaymentLink, "_self");
      }
      else if (isPaid) {
        const shortUrl = apiRes?.data?.data?.short_url;
        if (!shortUrl) {
          toast.error("Unable to proceed with the payment. Please contact support or try again after 15 minutes.");
          return;
        }
        openPaymentTab(shortUrl, apiRes?.data?.data?.invoice?.id);
      }
      else {
        toast.success(apiRes?.message || apiRes?.data?.message || 'Your subscription plan request received successfully waiting for payment confirmation and then upgrade your plan', {
          position: "bottom-right"
        })
        if (submitArg?.type === "amazonAds") {
          submitArg.action(true);
          navigate('/marketplace-accounts');
        }
        else if (submitArg) {
          submitArg.action(true);
        }
        else {
          navigate('/marketplace-accounts');
        }
      }
    }
    catch (err) {
      console.error('Failed to called addon invoice preview : ', err);
    }
    finally {
      setAddonPreviewData((prev) => ({
        ...prev,
        isLoading: false
      }))
    }
  }

  const onCloseAddOnInvoicePreview = (e, navigationUrl = null) => {
    e.preventDefault();
    setAddonPreviewData((prev) => ({
      ...prev,
      isOpenModal: false,
      previewData: null,
      isLoading: false
    }))
    if (navigationUrl) {
      navigate(navigationUrl)
    }
  }

  return (
    <RazorpayContext.Provider
      value={{
        razorpayModalData,
        setRazorpayModalData,
        invoiceModalData,
        setInvoiceModalData,
        handleCreatePlan,
        handlePreviewPlan,
        onClosePackageSummaryModal,
        handleFailed,
        addonInvoicePreviewData,
        handleAddOnInvoicePreview,
        handleAddOnSellerAccount,
        onCloseAddOnInvoicePreview,
        isRazorpayModalTimerLoader,
        setIsRazorpayModalTimerLoader,
        isPaymentStatus,
        setPaymentStatus
      }}
    >
      {children}
    </RazorpayContext.Provider>
  );

};

export const useRazorpay = () => useContext(RazorpayContext);